// Voice Forms tile (companion) — pick a bundled form → (pre-fill the typed fields) →
// dictate → the engine extracts every field → review/edit → send the branded report mail.
// Same engine + send path as the add-in (via /api/voiceforms proxies); compressed to a
// 3-step phone wizard: picker → run (fields + record) → review.
import { toast } from "./app";
import { loadSnap, saveSnap, skeletonHtml } from "./cache";
import { makeDropdown } from "./dropdown";
import { confirmDanger } from "./confirm";
import { fmtDate } from "./locale";

const $ = <T extends Element = HTMLElement>(sel: string): T | null => document.querySelector<T>(sel);

type Block = { id: string; label: string; hint: string; required: boolean; field_type: "dictate" | "date" | "choice" | "manual" | "list" | "contact"; enum_options: string[] | null };
// Org docgen forms (company client packs): review shows LIVE computed figures and send attaches
// the generated Excel/Word — same contract as the add-in (FormDef.docgen from /api/forms).
type Docgen = { variants: { key: string; label: string }[]; review: { name: string; label: string }[]; ranges?: Record<string, { min?: number; max?: number }> };
type FormDef = { id: string; name: string; category: string; info: { description: string }; blocks: Block[]; org?: boolean; docgen?: Docgen | null };
type FormSummary = { id: string; name: string; category: string; estMinutes: string; blockCount: number; org?: boolean };
type Cell = { covered: boolean; value: string };

// Saved runs (form_runs): every sent form lands there server-side; a draft is saved from the
// review screen. Reopening prefills review — editing + re-sending uses the exact same engine.
type RunSummary = { id: number; form_ref: string; form_name: string; title: string; status: "draft" | "sent"; variant?: string | null; last_sent_at: string | null; updated_at: string };
type RunFull = RunSummary & { answers: Record<string, string>; transcript: string; recipients: { to?: string; cc?: string; subject?: string } };

let forms: FormSummary[] = [];
let form: FormDef | null = null;
let result: { transcription?: { text?: string }; blocks?: Record<string, Cell> } | null = null;
let view: "forms" | "saved" = "forms";
let savedRuns: RunSummary[] = [];
let runsLoaded = false;
let currentRun: RunFull | null = null;   // reopened saved run (null = fresh flow)
let currentRunId: number | null = null;  // sticks across draft-save + send, so one run stays ONE row
const selfChecked = new Set<string>();   // "I've covered this" ticks — cosmetic, just for the user
const dds = new Map<string, { getValue: () => string }>();    // choice dropdowns on the run screen
const revDds = new Map<string, { getValue: () => string }>(); // choice dropdowns on the review screen
let variant = "";                                             // selected docgen variant key
let figWarnings: Array<{ id: string; message: string }> = []; // live range warnings (block send)
let figTimer: number | null = null;

// recording state (simple start/stop — Voice Memo owns the fancy recorder)
let media: MediaRecorder | null = null;
let stream: MediaStream | null = null;
let chunks: BlobPart[] = [];
let startedAt = 0;
let timerId: number | null = null;
let fmt = "webm";

const esc = (s: unknown): string => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));

// ── picker ──
// Stale-while-revalidate: paint the last-known picker (or a skeleton on a true first run)
// immediately, then fetch and re-render. The screen is never blanked while loading.
export async function loadForms(force = false): Promise<void> {
  if (forms.length && !force) { renderPicker(); return; }
  let painted = forms.length > 0;
  if (!forms.length) {
    const snap = loadSnap<FormSummary[]>("voiceforms");
    if (snap?.length) { forms = snap; painted = true; renderPicker(); }
    else { const list = $("#vfList"); if (list) list.innerHTML = skeletonHtml(4); }
  }
  try {
    const r = await fetch("/api/voiceforms", { credentials: "include" });
    if (r.status === 401) { renderEmpty("This phone isn't linked. Reconnect from Outlook."); return; }
    if (r.status === 402) { renderEmpty("Voice Forms are part of the Pro subscription — manage it in the Outlook add-in."); return; }
    if (!r.ok) throw new Error(String(r.status));
    const j = (await r.json()) as { forms?: FormSummary[] };
    forms = j.forms || [];
    saveSnap("voiceforms", forms);
    renderPicker();
    // warm every form DEFINITION in the background — a tap then opens with zero wait
    forms.slice(0, 12).forEach((f, i) => setTimeout(() => void fetchDef(f.id), 350 * (i + 1)));
  } catch {
    if (!painted) renderEmpty("Couldn't load the forms. Check your connection and try again.");
  }
}
function renderEmpty(msg: string): void {
  const list = $("#vfList"), empty = $("#vfEmpty");
  if (list) list.innerHTML = "";
  if (empty) { empty.textContent = msg; empty.style.display = "block"; }
}
// ── saved runs list ──
async function loadRuns(force = false): Promise<void> {
  if (runsLoaded && !force) { if (view === "saved") renderPicker(); return; }
  if (!savedRuns.length) {
    const snap = loadSnap<RunSummary[]>("vfruns");
    if (snap?.length) { savedRuns = snap; if (view === "saved") renderPicker(); }
  }
  try {
    const r = await fetch("/api/voiceforms/runs", { credentials: "include" });
    if (!r.ok) throw new Error(String(r.status));
    const j = (await r.json()) as { runs?: RunSummary[] };
    savedRuns = j.runs || [];
    runsLoaded = true;
    saveSnap("vfruns", savedRuns);
    if (view === "saved") renderPicker();
  } catch { if (view === "saved" && !savedRuns.length) renderEmpty("Couldn't load your saved forms. Check your connection and try again."); }
}
const fmtDay = (iso: string | null | undefined): string => fmtDate(iso); // shared EU/US locale setting

function renderSaved(): void {
  const list = $("#vfList"), empty = $("#vfEmpty");
  if (!list) return;
  const term = ($("#vfSearch") as HTMLInputElement | null)?.value.trim().toLowerCase() || "";
  const clear = $("#vfClear") as HTMLElement | null;
  if (clear) clear.hidden = !term;
  const count = $("#topCount");
  if (count && $("#screen-voiceforms")?.classList.contains("active")) { count.textContent = savedRuns.length ? String(savedRuns.length) : ""; count.hidden = !savedRuns.length; }
  const items = term ? savedRuns.filter((r) => `${r.title} ${r.form_name}`.toLowerCase().includes(term)) : savedRuns;
  if (!items.length) {
    list.innerHTML = "";
    if (empty) {
      empty.textContent = term ? "No saved forms match." : "Nothing saved yet — every form you send lands here, and you can save a draft from the review screen.";
      empty.style.display = "block";
    }
    return;
  }
  if (empty) empty.style.display = "none";
  list.innerHTML = items
    .map((r) =>
      `<div style="display:flex;align-items:center;gap:4px">` +
      `<button class="vf-row" data-run="${r.id}" style="flex:1;min-width:0">` +
      `<span class="mn-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="3" width="14" height="18" rx="2"/><path d="M9 7h6M9 11h6M9 15h3"/></svg></span>` +
      `<span class="mn-info"><span class="mn-title">${esc(r.title || r.form_name)}</span><span class="mn-sub">${esc(r.form_name)} · ${r.status === "sent" ? `Sent ${esc(fmtDay(r.last_sent_at || r.updated_at))}` : `Draft · ${esc(fmtDay(r.updated_at))}`}</span></span>` +
      `<span class="crow-chev"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg></span>` +
      `</button>` +
      `<button class="cd-iconbtn" data-rundel="${r.id}" title="Delete"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg></button>` +
      `</div>`,
    )
    .join("");
}

async function openSavedRun(id: number): Promise<void> {
  const summary = savedRuns.find((r) => r.id === id);
  try {
    const rr = await fetch(`/api/voiceforms/runs?id=${id}`, { credentials: "include" });
    if (!rr.ok) throw new Error(String(rr.status));
    const run = (await rr.json()) as RunFull;
    const def = loadSnap<FormDef>("vform:" + run.form_ref) || (await fetchDef(run.form_ref));
    if (!def) { toast("Couldn't reopen — the form behind it may have been deleted"); return; }
    form = def;
    currentRun = run;
    currentRunId = run.id;
    const blocks: Record<string, Cell> = {};
    def.blocks.forEach((b) => { const v = run.answers[b.id] || ""; blocks[b.id] = { covered: v.trim() !== "", value: v }; });
    result = { blocks, transcription: { text: run.transcript || "" } };
    openReview();
  } catch { toast(summary ? `Couldn't open “${summary.title || summary.form_name}”` : "Couldn't open that saved form"); }
}

async function deleteSavedRun(id: number): Promise<void> {
  const r = savedRuns.find((x) => x.id === id);
  const ok = await confirmDanger({
    title: "Delete this saved form?",
    body: `“${r?.title || r?.form_name || "This saved form"}” will be gone for good — emails and documents you already sent stay with their recipients.`,
    cta: "Delete",
  });
  if (!ok) return;
  try {
    const res = await fetch(`/api/voiceforms/runs?id=${id}`, { method: "DELETE", credentials: "include" });
    if (!res.ok) throw new Error(String(res.status));
    savedRuns = savedRuns.filter((x) => x.id !== id);
    saveSnap("vfruns", savedRuns);
    renderPicker();
  } catch { toast("Couldn't delete — try again"); }
}

function renderPicker(): void {
  if (view === "saved") { renderSaved(); return; }
  const list = $("#vfList"), empty = $("#vfEmpty");
  if (!list) return;
  const term = ($("#vfSearch") as HTMLInputElement | null)?.value.trim().toLowerCase() || "";
  const clear = $("#vfClear") as HTMLElement | null;
  if (clear) clear.hidden = !term;

  // Active-screen gate: the Home-boot prefetch renders in the background (see contacts.ts).
  const count = $("#topCount");
  if (count && $("#screen-voiceforms")?.classList.contains("active")) { count.textContent = forms.length ? String(forms.length) : ""; count.hidden = !forms.length; }

  const items = term
    ? forms.filter((f) => `${f.name} ${f.category}`.toLowerCase().includes(term))
    : forms;
  if (!items.length) {
    list.innerHTML = "";
    if (empty) { empty.textContent = term ? "No forms match." : "No forms yet."; empty.style.display = "block"; }
    return;
  }
  if (empty) empty.style.display = "none";
  list.innerHTML = items
    .map((f) =>
      `<button class="vf-row" data-form="${esc(f.id)}">` +
      `<span class="mn-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="3" width="14" height="18" rx="2"/><path d="M9 7h6M9 11h6M9 15h3"/></svg></span>` +
      `<span class="mn-info"><span class="mn-title">${esc(f.name)}${f.org ? ` <span class="vf-chip-company">Company</span>` : ""}</span><span class="mn-sub">${esc(f.category)} · ~${esc(f.estMinutes)} min · ${f.blockCount} fields</span></span>` +
      `<span class="crow-chev"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg></span>` +
      `</button>`,
    )
    .join("");
}

// ── run screen (typed fields + the dictation checklist + record) ──
/** Fetch one form definition and refresh its snapshot (defs change rarely — the
 *  desktop Form Builder is the editor; the cache makes a tap open instantly). */
async function fetchDef(id: string): Promise<FormDef | null> {
  try {
    const r = await fetch(`/api/voiceforms?id=${encodeURIComponent(id)}`, { credentials: "include" });
    if (!r.ok) return null;
    const def = (await r.json()) as FormDef;
    saveSnap("vform:" + id, def);
    return def;
  } catch { return null; }
}

async function openRun(id: string): Promise<void> {
  currentRun = null; currentRunId = null; // fresh flow — a NEW run row is created on save/send
  selfChecked.clear();
  // snapshot-first: paint the run screen on the spot; the fetch refreshes the cache
  // for the NEXT open (never rebuild a screen the user is already filling in)
  const cached = loadSnap<FormDef>("vform:" + id);
  if (cached) {
    form = cached;
    renderRun();
    void fetchDef(id);
    return;
  }
  const def = await fetchDef(id);
  if (!def) { toast("Couldn't open that form"); return; }
  form = def;
  renderRun();
}

// Numeric bounds for a field (from the org pack) → decimal keypad + visible "Allowed range".
function rangeOf(b: Block): { min?: number; max?: number } | null {
  if (b.field_type === "choice" || b.field_type === "date") return null;
  return form?.docgen?.ranges?.[b.id] ?? null;
}
const rangeText = (r: { min?: number; max?: number }): string =>
  r.min != null && r.max != null ? `${r.min}–${r.max}` : r.min != null ? `≥ ${r.min}` : r.max != null ? `≤ ${r.max}` : "";
// Flemish/EU numbers: "15.000" is a thousands dot (15000), never 15 — same rules as the server parser.
function humanNum(raw: string): number {
  let s = raw.trim().replace(/[€\s]/g, "");
  if (!s) return NaN;
  const d = s.lastIndexOf("."), c = s.lastIndexOf(",");
  if (d > -1 && c > -1) s = c > d ? s.replace(/\./g, "").replace(",", ".") : s.replace(/,/g, "");
  else if (c > -1) s = s.length - c - 1 === 3 && c > 0 ? s.replace(/,/g, "") : s.replace(",", ".");
  else if (d > -1 && s.length - d - 1 === 3 && d > 0) s = s.replace(/\./g, "");
  return parseFloat(s);
}
function markRange(b: Block, el: HTMLInputElement): void {
  const r = rangeOf(b); if (!r) return;
  const v = el.value.trim();
  if (v === "") { el.style.borderColor = ""; return; }
  const n = humanNum(v);
  el.style.borderColor = Number.isNaN(n) || (r.min != null && n < r.min) || (r.max != null && n > r.max) ? "var(--danger, #e5484d)" : "";
}

// list/contact carry ONE ITEM PER LINE — a list token repeats its block in the Word template,
// so the line count is the point. contact fields also offer a pick from the cached contacts
// (custom .rp-dd with search — never a native select).
type VfContact = { name?: string; email?: string; company?: string };
const CHECK_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>`;
/** Compact field that grows to fit its content (capped, so the record button stays reachable). */
function grow(el: HTMLTextAreaElement): void {
  el.style.height = "auto";
  const want = el.scrollHeight;
  el.style.height = `${Math.min(want, 200)}px`;
  // Only scroll once we hit the cap — at an exact fit the browser still paints a scrollbar sliver.
  el.style.overflowY = want > 200 ? "auto" : "hidden";
}
/** A placeholder does not count towards scrollHeight, so measure it as if it were the value —
 *  otherwise a hint that wraps to two lines gets clipped in a one-line compact field. */
function growToPlaceholder(el: HTMLTextAreaElement): void {
  if (el.value.trim()) { grow(el); return; }
  if (!el.placeholder) return;
  el.value = el.placeholder;
  grow(el);
  el.value = "";
}
const perLinePh = (b: Block): string => b.hint || (b.field_type === "list" ? "One point per line" : "One name per line");

/** Mount an "add from contacts" dropdown under a contact field's textarea. */
function mountContactPicker(host: HTMLElement, area: HTMLTextAreaElement): void {
  const all = (loadSnap<VfContact[]>("contacts") || []).filter((c) => c.name || c.email);
  if (!all.length) return;
  const wrap = document.createElement("div");
  wrap.className = "vf-dd";
  const dd = makeDropdown({
    placeholder: "+ Add from contacts", value: "",
    options: all.slice(0, 300).map((c) => {
      const label = c.name || c.email || "";
      return { value: label, label: c.company ? `${label} · ${c.company}` : label };
    }),
    onChange: (v) => {
      if (!v) return;
      const cur = area.value.split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
      if (!cur.includes(v)) area.value = [...cur, v].join("\n");
      area.dispatchEvent(new Event("input", { bubbles: true }));
    },
  });
  wrap.appendChild(dd.el);
  host.appendChild(wrap);
}

function renderRun(): void {
  if (!form) return;
  const t = $("#vfRunTitle"); if (t) t.textContent = form.name;
  const body = $("#vfRunFields");
  if (body) {
    dds.clear();
    body.innerHTML = form.blocks
      .map((b) => {
        const r = rangeOf(b);
        // Every field looks the same: a tick you can flip while dictating, the label, then the
        // widget. Every field is BOTH dictatable and typeable — the type only picks the widget,
        // so there is no mic/pencil marking to imply otherwise.
        // Label row + (like the pane) the hint on its OWN line — a long hint inside a one-line
        // compact field would be clipped, and the field is where you type, not where you read.
        // The hint lives IN the field as its placeholder — showing it twice reads as noise
        // (Gery 2026-08-20). Only choose/date carry no placeholder, so those keep the line.
        const noPlaceholder = b.field_type === "choice" || b.field_type === "date";
        const head = `<div class="vf-fhead"><button type="button" class="vf-chk" data-vfchk="${esc(b.id)}" title="Tick what you've covered"></button><label>${esc(b.label)}${b.required ? ' <span class="vf-req">*</span>' : ""}</label></div>`
          + (noPlaceholder && b.hint ? `<div class="vf-fhint">${esc(b.hint)}</div>` : "");
        if (r) return `<div class="rp-field">${head}<span class="vf-rangehint">Allowed range: ${esc(rangeText(r))}</span><input data-vfin="${esc(b.id)}" inputmode="decimal" placeholder="${esc(b.hint)}" /></div>`;
        if (b.field_type === "choice") return `<div class="rp-field">${head}<div class="vf-dd" data-vfdd="${esc(b.id)}"></div></div>`;
        if (b.field_type === "date") return `<div class="rp-field">${head}<input data-vfin="${esc(b.id)}" type="date" /></div>`;
        if (b.field_type === "manual") return `<div class="rp-field">${head}<input data-vfin="${esc(b.id)}" placeholder="${esc(b.hint)}" /></div>`;
        if (b.field_type === "contact") return `<div class="rp-field">${head}<textarea class="vf-compact" data-vfin="${esc(b.id)}" rows="1" placeholder="${esc(perLinePh(b))}"></textarea><div data-vfpick="${esc(b.id)}"></div></div>`;
        // dictate + list: one line tall, grows the moment you tap it — compact on a phone, but
        // typeable like everything else (it used to be a read-only reminder line).
        return `<div class="rp-field">${head}<textarea class="vf-compact" data-vfin="${esc(b.id)}" rows="1" placeholder="${esc(b.field_type === "list" ? perLinePh(b) : b.hint || "Say it, or type it")}"></textarea></div>`;
      })
      .join("");
    // live out-of-range border on the numeric fields
    body.querySelectorAll<HTMLInputElement>("[data-vfin]").forEach((el) => {
      const b = form!.blocks.find((x) => x.id === el.getAttribute("data-vfin"));
      if (b && rangeOf(b)) el.addEventListener("input", () => markRange(b, el));
    });
    // compact fields grow to fit while you use them, and fold back only when left empty
    body.querySelectorAll<HTMLTextAreaElement>("textarea.vf-compact").forEach((el) => {
      growToPlaceholder(el); // at rest the box is exactly as tall as its hint — usually one line
      el.addEventListener("focus", () => grow(el));
      el.addEventListener("input", () => grow(el));
      el.addEventListener("blur", () => { if (!el.value.trim()) el.style.height = ""; });
    });
    // personal ticks — purely a checklist for the person dictating
    body.querySelectorAll<HTMLButtonElement>("[data-vfchk]").forEach((btn) => {
      const id = btn.getAttribute("data-vfchk")!;
      btn.classList.toggle("on", selfChecked.has(id));
      btn.innerHTML = selfChecked.has(id) ? CHECK_SVG : "";
      btn.addEventListener("click", () => {
        if (selfChecked.has(id)) selfChecked.delete(id); else selfChecked.add(id);
        btn.classList.toggle("on", selfChecked.has(id));
        btn.innerHTML = selfChecked.has(id) ? CHECK_SVG : "";
      });
    });
    // mount the choice dropdowns (custom .rp-dd — never a native select)
    body.querySelectorAll<HTMLElement>("[data-vfdd]").forEach((host) => {
      const blockId = host.getAttribute("data-vfdd")!;
      const b = form!.blocks.find((x) => x.id === blockId);
      const dd = makeDropdown({ placeholder: "— pick —", value: "", options: [{ value: "", label: "— pick —" }, ...(b?.enum_options || []).map((o) => ({ value: o, label: o }))] });
      host.appendChild(dd.el);
      dds.set(blockId, dd);
    });
    body.querySelectorAll<HTMLElement>("[data-vfpick]").forEach((host) => {
      const area = body.querySelector<HTMLTextAreaElement>(`[data-vfin="${CSS.escape(host.getAttribute("data-vfpick")!)}"]`);
      if (area) mountContactPicker(host, area);
    });
  }
  setRec("idle");
  $("#vfRun")?.classList.add("open");
}
function closeRun(): void { stopMedia(); $("#vfRun")?.classList.remove("open"); }

// ── recording (start/stop toggle) ──
function setRec(s: "idle" | "rec" | "busy"): void {
  $("#vfRun")?.setAttribute("data-rec", s);
  const h = $("#vfRecHint");
  if (h) h.textContent = s === "rec" ? "Recording — tap to finish" : s === "busy" ? "Building the report…" : "Tap to record";
  if (s !== "rec") { const t = $("#vfTimer"); if (t) t.textContent = "0:00"; }
}
function pickMime(): { mime: string; f: string } {
  const cands = [
    { mime: "audio/webm;codecs=opus", f: "webm" }, { mime: "audio/webm", f: "webm" },
    { mime: "audio/mp4", f: "mp4" }, { mime: "audio/ogg;codecs=opus", f: "ogg" },
  ];
  for (const c of cands) if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(c.mime)) return c;
  return { mime: "", f: "webm" };
}
function stopMedia(): void {
  if (timerId) { clearTimeout(timerId); timerId = null; }
  try { if (media && media.state !== "inactive") media.stop(); } catch { /* ignore */ }
  stream?.getTracks().forEach((t) => t.stop());
  stream = null; media = null;
}
async function toggleRec(): Promise<void> {
  const state = $("#vfRun")?.getAttribute("data-rec");
  if (state === "busy") return;
  if (state === "rec") { // stop → process
    if (timerId) { clearTimeout(timerId); timerId = null; }
    try { media?.stop(); } catch { /* onstop handles it */ }
    return;
  }
  try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
  catch { toast("We couldn't access your microphone"); return; }
  const { mime, f } = pickMime();
  fmt = f; chunks = [];
  try { media = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream); }
  catch { media = new MediaRecorder(stream!); }
  media.ondataavailable = (ev) => { if (ev.data?.size) chunks.push(ev.data); };
  media.onstop = () => void process();
  media.start();
  startedAt = Date.now();
  const tick = (): void => {
    const s = Math.floor((Date.now() - startedAt) / 1000);
    const el = $("#vfTimer"); if (el) el.textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
    timerId = window.setTimeout(tick, 300);
  };
  tick();
  setRec("rec");
}

function collectInputs(): Record<string, string> {
  const inputs: Record<string, string> = {};
  document.querySelectorAll<HTMLInputElement>("#vfRunFields [data-vfin]").forEach((el) => {
    const v = el.value.trim(); if (v) inputs[el.getAttribute("data-vfin")!] = v;
  });
  dds.forEach((dd, id) => { const v = dd.getValue(); if (v) inputs[id] = v; });
  return inputs;
}

async function process(): Promise<void> {
  const blob = new Blob(chunks);
  chunks = [];
  stream?.getTracks().forEach((t) => t.stop()); stream = null; media = null;
  if (!form) return;
  if (blob.size < 1200) { setRec("idle"); toast("That was too short — try again"); return; }
  setRec("busy");
  try {
    const b64 = await new Promise<string>((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => { const s = String(fr.result || ""); res(s.slice(s.indexOf(",") + 1)); };
      fr.onerror = () => rej(new Error("read_failed"));
      fr.readAsDataURL(blob);
    });
    const r = await fetch("/api/voiceforms/process", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ form_id: form.id, audio_b64: b64, audio_format: fmt, inputs: collectInputs() }),
    });
    const j = (await r.json().catch(() => ({}))) as { ok?: boolean; blocks?: Record<string, Cell>; transcription?: { text?: string }; error?: { message?: string } | string };
    setRec("idle");
    if (!r.ok || !j.ok) {
      const msg = typeof j.error === "object" && j.error?.message ? j.error.message : "Couldn't process the recording — try again.";
      toast(msg);
      return;
    }
    result = j;
    openReview();
  } catch { setRec("idle"); toast("Couldn't process — check your connection"); }
}

// ── review + send ──
// Field-type-aware review (matches the add-in wizard): choice → dropdown (a typed free value can
// never reach an org price sheet), date → date input, numeric → decimal input with range guard,
// everything else the editable textarea.
function openReview(): void {
  if (!form || !result) return;
  variant = currentRun?.variant || form.docgen?.variants?.[0]?.key || "";
  figWarnings = [];
  const body = $("#vfReviewFields");
  if (body) {
    revDds.clear();
    body.innerHTML = form.blocks
      .map((b) => {
        const cell = result!.blocks?.[b.id] || { covered: false, value: "" };
        const miss = !cell.covered && b.required ? `<span class="vf-miss">Not covered</span>` : "";
        const lab = `<label>${esc(b.label)} ${miss}</label>`;
        const r = rangeOf(b);
        if (b.field_type === "choice") return `<div class="rp-field">${lab}<div class="vf-dd" data-vfrevdd="${esc(b.id)}"></div></div>`;
        if (b.field_type === "date") return `<div class="rp-field">${lab}<input data-vfrev="${esc(b.id)}" type="date" value="${esc(cell.value)}" /></div>`;
        if (r) return `<div class="rp-field">${lab}<span class="vf-rangehint">Allowed range: ${esc(rangeText(r))}</span><input data-vfrev="${esc(b.id)}" inputmode="decimal" value="${esc(cell.value)}" /></div>`;
        if (b.field_type === "list" || b.field_type === "contact") {
          const lines = Math.min(10, Math.max(2, cell.value.split(/\r?\n/).length + (b.field_type === "list" ? 1 : 0)));
          return `<div class="rp-field">${lab}<textarea data-vfrev="${esc(b.id)}" rows="${lines}" placeholder="${esc(perLinePh(b))}">${esc(cell.value)}</textarea>`
            + (b.field_type === "contact" ? `<div data-vfrevpick="${esc(b.id)}"></div>` : "")
            + `</div>`;
        }
        return `<div class="rp-field">${lab}<textarea data-vfrev="${esc(b.id)}" rows="${cell.value.length > 80 ? 3 : 1}">${esc(cell.value)}</textarea></div>`;
      })
      .join("");
    // mount review choice-dropdowns, pre-set to the extracted value
    body.querySelectorAll<HTMLElement>("[data-vfrevdd]").forEach((host) => {
      const blockId = host.getAttribute("data-vfrevdd")!;
      const b = form!.blocks.find((x) => x.id === blockId);
      const cur = result!.blocks?.[blockId]?.value || "";
      const dd = makeDropdown({
        placeholder: "— pick —", value: cur,
        options: [{ value: "", label: "— pick —" }, ...(b?.enum_options || []).map((o) => ({ value: o, label: o }))],
        onChange: () => scheduleFigures(),
      });
      host.appendChild(dd.el);
      revDds.set(blockId, dd);
    });
    body.querySelectorAll<HTMLElement>("[data-vfrevpick]").forEach((host) => {
      const area = body.querySelector<HTMLTextAreaElement>(`[data-vfrev="${CSS.escape(host.getAttribute("data-vfrevpick")!)}"]`);
      if (area) mountContactPicker(host, area);
    });
    // any edit refreshes the live figures (org docgen forms only) + the range border
    body.querySelectorAll<HTMLInputElement>("[data-vfrev]").forEach((el) => {
      const b = form!.blocks.find((x) => x.id === el.getAttribute("data-vfrev"));
      el.addEventListener("input", () => { if (b && rangeOf(b)) markRange(b, el); scheduleFigures(); });
    });
  }
  const figs = $("#vfFigures");
  if (figs) {
    figs.hidden = !(form.org && form.docgen);
    // variant picker — only when the pack has more than one (e.g. H200.0 vs H200.1)
    figs.querySelector(".vf-variant")?.remove();
    const variants = form.docgen?.variants || [];
    if (form.org && variants.length > 1) {
      const host = document.createElement("div");
      host.className = "vf-variant";
      const dd = makeDropdown({
        placeholder: variants[0].label, value: variant,
        options: variants.map((v) => ({ value: v.key, label: v.label })),
        onChange: (v) => { variant = v; scheduleFigures(); },
      });
      host.appendChild(dd.el);
      figs.prepend(host);
    }
  }
  if (form.org && form.docgen) void refreshFigures();
  // Default recipient = last-used for this form, else the signed-in user (matches the add-in —
  // never leave To blank, or a tapped Send silently stops at "Add at least one recipient").
  const myEmail = loadSnap<{ email?: string }>("session")?.email || "";
  // reopened saved run: its stored recipients/subject win over the per-form defaults
  ($("#vfTo") as HTMLInputElement | null)!.value = currentRun?.recipients?.to || loadSnap<string>("vfrecip:" + form.id) || myEmail;
  ($("#vfSubject") as HTMLInputElement | null)!.value = currentRun?.recipients?.subject || currentRun?.title || form.name;
  $("#vfReview")?.classList.add("open");
}
function closeReview(): void { $("#vfReview")?.classList.remove("open"); }

// current review values, keyed by block id (answers for the deterministic engine)
function collectReview(): Record<string, string> {
  const answers: Record<string, string> = {};
  form?.blocks.forEach((b) => {
    if (b.field_type === "choice") { answers[b.id] = revDds.get(b.id)?.getValue() || ""; return; }
    const el = document.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[data-vfrev="${CSS.escape(b.id)}"]`);
    answers[b.id] = el?.value.trim() || "";
  });
  return answers;
}

// ── live computed figures (org docgen forms) — debounced; the engine is deterministic and fast ──
function scheduleFigures(): void {
  if (!form?.org || !form.docgen) return;
  $("#vfFigRow")?.classList.add("stale");
  if (figTimer) clearTimeout(figTimer);
  figTimer = window.setTimeout(() => void refreshFigures(), 600);
}
async function refreshFigures(): Promise<void> {
  if (!form?.org || !form.docgen) return;
  try {
    const r = await fetch("/api/voiceforms/docgen-preview", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ id: form.id, answers: collectReview(), variant }),
    });
    const j = (await r.json().catch(() => null)) as { ok?: boolean; outputs?: Array<{ name: string; label: string; value: string }>; warnings?: Array<{ id: string; message: string }> } | null;
    if (!j?.ok) return;
    const row = $("#vfFigRow");
    if (row) {
      row.classList.remove("stale");
      row.innerHTML = (j.outputs || []).map((o) => `<div class="vf-fig"><span class="l">${esc(o.label)}</span><span class="v">${esc(o.value)}</span></div>`).join("");
    }
    figWarnings = j.warnings || [];
    const warn = $("#vfFigWarn");
    if (warn) { warn.hidden = !figWarnings.length; warn.textContent = figWarnings.length ? `⚠ ${figWarnings.map((w) => w.message).join(" ")}` : ""; }
    // paint the offending fields
    form.blocks.forEach((b) => {
      const el = document.querySelector<HTMLElement>(`[data-vfrev="${CSS.escape(b.id)}"]`);
      if (el) el.style.borderColor = figWarnings.some((w) => w.id === b.id) ? "var(--danger, #e5484d)" : el.style.borderColor;
    });
  } catch { /* figures are advisory; send still validates server-side */ }
}

// The run payload sent with EVERY send + used by Save draft: same id = same row, so one form
// stays ONE saved object (draft → sent → edited → re-sent), never a trail of copies.
function runPayload(): Record<string, unknown> {
  const subject = (($("#vfSubject") as HTMLInputElement | null)?.value || form?.name || "").trim();
  return {
    ...(currentRunId != null ? { id: currentRunId } : {}),
    form_ref: form!.id,
    form_name: form!.name,
    title: subject || form!.name,
    answers: collectReview(),
    ...(variant ? { variant } : {}),
    transcript: result?.transcription?.text || "",
    recipients: { to: ($("#vfTo") as HTMLInputElement | null)?.value || "", cc: "", subject },
  };
}

async function saveDraft(): Promise<void> {
  if (!form) return;
  const btn = $("#vfSaveDraft") as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = "Saving…"; }
  try {
    const r = await fetch("/api/voiceforms/runs", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify(runPayload()),
    });
    const j = (await r.json().catch(() => ({}))) as { ok?: boolean; id?: number };
    if (!r.ok || !j.ok) { toast("Couldn't save — try again"); return; }
    if (j.id != null) currentRunId = j.id;
    toast("Saved — find it under Saved");
    void loadRuns(true);
  } catch { toast("Couldn't save — check your connection"); }
  finally { if (btn) { btn.disabled = false; btn.textContent = "Save draft — send later"; } }
}

async function send(): Promise<void> {
  if (!form) return;
  // The session email shown in the app is MASKED by design (g···@…) — the server swaps the
  // "me" sentinel for the real address, so a masked prefill still delivers to the user.
  const to = (($("#vfTo") as HTMLInputElement | null)?.value || "").split(/[,;\s]+/)
    .map((s) => s.trim()).filter((s) => s.includes("@"))
    .map((s) => (s.includes("·") || s.includes("•") || s.includes("*") ? "me" : s));
  if (!to.length) { toast("Add at least one recipient"); return; }
  if (figWarnings.length) { toast("Fix the highlighted values first"); return; } // out-of-range never becomes a quote
  const answers = collectReview();
  const sections: Array<{ label: string; value: string }> = [];
  form.blocks.forEach((b) => { const v = answers[b.id]; if (v) sections.push({ label: b.label, value: v }); });
  if (!sections.length) { toast("The report is empty"); return; }
  const btn = $("#vfSend") as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = "Sending…"; }
  try {
    const r = await fetch("/api/voiceforms/send", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({
        to,
        subject: (($("#vfSubject") as HTMLInputElement | null)?.value || form.name).trim(),
        form_name: form.name,
        sections,
        transcript: result?.transcription?.text || "",
        // org docgen forms: the backend regenerates the documents from these confirmed answers
        // (deterministic engine) and attaches the filled Excel/Word to the mail
        ...(form.org && form.docgen ? { org_form_id: form.id, answers, variant } : {}),
        run: runPayload(), // the backend stamps this run 'sent' in the same request
      }),
    });
    const j = (await r.json().catch(() => ({}))) as { ok?: boolean; error?: string; detail?: string; errorMessage?: string };
    if (!r.ok || !j.ok) {
      // surface the REAL reason (docgen_failed / send_failed detail / upstream timeout) — a
      // generic message hides which leg of the chain died
      const why = j.detail || j.errorMessage || (typeof j.error === "string" ? j.error : "");
      toast(why ? `Couldn't send — ${why.slice(0, 120)}` : "Couldn't send — try again");
      return;
    }
    saveSnap("vfrecip:" + form.id, to.join(", ")); // remember this recipient for next time (per form)
    closeReview(); closeRun();
    currentRun = null; currentRunId = null;
    void loadRuns(true); // the send landed under Saved (status 'sent') — refresh the list
    toast(`Report sent to ${to.length} recipient${to.length === 1 ? "" : "s"}`);
  } catch { toast("Couldn't send — check your connection"); }
  finally { if (btn) { btn.disabled = false; btn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 2 11 13"/><path d="M22 2 15 22l-4-9-9-4z"/></svg> Send report`; } }
}

// ── wiring ──
export function initVoiceForms(): void {
  const search = $("#vfSearch") as HTMLInputElement | null;
  search?.addEventListener("input", renderPicker);
  $("#vfClear")?.addEventListener("click", () => { if (search) search.value = ""; renderPicker(); search?.focus(); });
  // Forms | Saved toggle — Saved lists the user's runs (drafts + sent), tap to reopen & re-send.
  $("#vfSeg")?.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-v]");
    if (!b) return;
    view = b.getAttribute("data-v") === "saved" ? "saved" : "forms";
    document.querySelectorAll<HTMLElement>("#vfSeg [data-v]").forEach((x) => x.classList.toggle("on", x === b));
    if (search) { search.value = ""; search.placeholder = view === "saved" ? "Search saved forms…" : "Search forms…"; }
    renderPicker();
    if (view === "saved") void loadRuns();
  });
  $("#vfList")?.addEventListener("click", (e) => {
    const del = (e.target as HTMLElement).closest<HTMLElement>("[data-rundel]");
    if (del) { void deleteSavedRun(Number(del.getAttribute("data-rundel"))); return; }
    const runRow = (e.target as HTMLElement).closest<HTMLElement>("[data-run]");
    if (runRow) { void openSavedRun(Number(runRow.getAttribute("data-run"))); return; }
    const row = (e.target as HTMLElement).closest<HTMLElement>("[data-form]");
    if (row) void openRun(row.getAttribute("data-form")!);
  });
  $("#vfRunBack")?.addEventListener("click", closeRun);
  $("#vfRecBtn")?.addEventListener("click", () => void toggleRec());
  $("#vfReviewBack")?.addEventListener("click", closeReview);
  $("#vfSend")?.addEventListener("click", () => void send());
  $("#vfSaveDraft")?.addEventListener("click", () => void saveDraft());
}
